import React, { useEffect, useState } from "react";
import { FaSpinner } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Batch = {
  id: number;
  name: string;
  topic: string;
};

type TaskSummary = {
  taskId: number;
  taskNumber: number;
  level: string;
};

type StudentTaskScore = {
  taskId: number;
  score: number | null;
  completed: boolean;
};

type BatchStudentTaskSummary = {
  studentId: number;
  firstName: string;
  lastName: string;
  results: StudentTaskScore[];
};

type BatchTaskSummaryResponse = {
  tasks: TaskSummary[];
  students: BatchStudentTaskSummary[];
};

const MentorBatchWritingTaskSummary: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [taskSummary, setTaskSummary] =
    useState<BatchTaskSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    setLoadingBatches(true);
    setError("");
    try {
      // Fetch mentor's own batches (not all batches)
      const response = await fetch(`${API_BASE_URL}/api/mentor/my-batches`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        // Map the response to extract id, name, and topic
        const batchList = data.map((b: any) => ({
          id: b.id,
          name: b.name,
          topic: b.topic,
        }));
        setBatches(batchList);
        // Auto-select first batch
        if (batchList.length > 0) {
          setSelectedBatchId(batchList[0].id);
        }
      } else {
        setError("Failed to load batches");
      }
    } catch (err) {
      console.error("Error fetching batches:", err);
      setError("Failed to load batches");
    } finally {
      setLoadingBatches(false);
    }
  };

  useEffect(() => {
    if (!selectedBatchId) {
      setTaskSummary(null);
      return;
    }
    fetchTaskSummary(selectedBatchId);
  }, [selectedBatchId]);

  const fetchTaskSummary = async (batchId: number) => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/writing-tasks/batches/${batchId}/task-summary`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setTaskSummary(data);
      } else {
        setTaskSummary(null);
        setError("Failed to load task summary");
      }
    } catch (err) {
      console.error("Error fetching task summary:", err);
      setTaskSummary(null);
      setError("Failed to load task summary");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Batch Selector */}
      <div className="mb-6">
        <label className="font-bold text-gray-700 mr-3">Select Batch:</label>
        <select
          className="border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
          style={{ fontSize: "16px" }}
          onChange={(e) => setSelectedBatchId(Number(e.target.value))}
          value={selectedBatchId ?? ""}
          disabled={loadingBatches}
        >
          {loadingBatches ? (
            <option>Loading...</option>
          ) : (
            <>
              {batches.length === 0 && (
                <option value="">No batches available</option>
              )}
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.topic})
                </option>
              ))}
            </>
          )}
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
          <span className="ml-3 text-gray-600">Loading task summary...</span>
        </div>
      ) : selectedBatchId && taskSummary ? (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border font-sans text-sm">
            <thead>
              <tr className="bg-[#009fe3] text-white">
                <th className="border px-4 py-2 text-left font-semibold">
                  Student
                </th>
                {taskSummary.tasks.map((task) => (
                  <th
                    key={task.taskId}
                    className="border px-4 py-2 text-center font-semibold"
                  >
                    Task #{task.taskNumber}
                    <div className="text-xs font-normal opacity-90">
                      ({task.level})
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {taskSummary.students.length === 0 ? (
                <tr>
                  <td
                    colSpan={taskSummary.tasks.length + 1}
                    className="border px-4 py-8 text-center text-gray-400"
                  >
                    No students enrolled in this batch
                  </td>
                </tr>
              ) : (
                taskSummary.students.map((student) => (
                  <tr key={student.studentId} className="hover:bg-blue-50">
                    <td className="border px-4 py-2 font-semibold text-gray-900">
                      {student.firstName} {student.lastName}
                    </td>
                    {taskSummary.tasks.map((task) => {
                      const result = student.results.find(
                        (r) => r.taskId === task.taskId
                      );

                      if (!result) {
                        return (
                          <td
                            key={task.taskId}
                            className="border px-4 py-2 text-center"
                          >
                            <span className="text-gray-400">—</span>
                          </td>
                        );
                      }

                      return (
                        <td
                          key={task.taskId}
                          className="border px-4 py-2 text-center"
                        >
                          {result.completed && result.score !== null ? (
                            <span className="font-semibold text-green-600">
                              {result.score.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-red-500 font-medium">x</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && (
          <div className="text-center py-12 text-gray-500">
            {selectedBatchId
              ? "No task summary available for this batch."
              : "Please select a batch to view task summary."}
          </div>
        )
      )}
    </div>
  );
};

export default MentorBatchWritingTaskSummary;
